import type { ApiErrorBody } from "./types";

const BASE = `${process.env.REACT_APP_BACKEND_URL ?? ""}/api`;
const CSRF_COOKIE = "gpt_csrf";
const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

let csrfToken: string | null = null;
type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public requestId: string | null = null,
    public details: ApiErrorBody["error"]["details"] = undefined,
  ) {
    super(message);
  }
}

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

export function onUnauthorized(fn: Listener): () => void {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
}

function readCookie(name: string): string | null {
  const match = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function isErrorBody(body: unknown): body is ApiErrorBody {
  return typeof body === "object" && body !== null && "error" in body;
}

export async function request<T>(method: string, path: string, body?: unknown, extra?: Record<string, string>): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...(extra ?? {}) };
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (UNSAFE.has(method)) {
    const token = readCookie(CSRF_COOKIE) ?? csrfToken;
    if (token) headers["X-CSRF-Token"] = token;
  }
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: "include",
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Unable to reach the server. Check your connection.");
  }
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    if (isErrorBody(data)) {
      const e = data.error;
      if (res.status === 401 && path !== "/auth/session") unauthorizedListeners.forEach((fn) => fn());
      throw new ApiError(res.status, e.code, e.message, e.request_id, e.details);
    }
    throw new ApiError(res.status, "HTTP_ERROR", "Something went wrong. Please try again.");
  }
  if (data && typeof data === "object" && "csrf_token" in data) {
    setCsrfToken(String((data as { csrf_token: string }).csrf_token));
  }
  return data as T;
}

export const apiUrl = (path: string) => `${BASE}${path}`;

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "VALIDATION_ERROR" && err.details?.length) {
      return err.details.map((d) => `${d.field ? `${d.field}: ` : ""}${d.message}`).join(" ");
    }
    return err.message;
  }
  return "Something went wrong. Please try again.";
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown, opts?: { headers?: Record<string, string> }) => request<T>("POST", path, body ?? {}, opts?.headers),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  del: <T>(path: string) => request<T>("DELETE", path),
};

export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || !err.details) return {};
  return Object.fromEntries(err.details.map((d) => [d.field, d.message.replace(/^Value error, /, "")]));
}
